import {
  AlertTriangle,
  BookOpen,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  FileText,
  Shield,
} from 'lucide-react'
import { useState } from 'react'
import { PoweredByFooter } from '@/components/PoweredByFooter'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { trackEvent } from '@/lib/analytics'

interface GuideSection {
  id: string
  title: string
  icon: React.ElementType
  description: string
  content: string
}

interface ChecklistItem {
  id: string
  label: string
  checked: boolean
}

const guides: GuideSection[] = [
  {
    id: 'agency',
    title: 'Agency Agreements',
    icon: FileText,
    description: 'Requirements for valid agency agreements',
    content: `**Agency Agreement Requirements**

Before you can market a property, you must have a signed agency agreement in place. Key requirements include:

• **Parties identified** — Full names and contact details of all vendors
• **Property details** — Complete legal description and physical address
• **Commission structure** — Clearly stated percentage or fixed amount
• **Marketing authority** — Written consent for advertising methods
• **Duration** — Start date and either end date or conditions for termination
• **Sole or general** — Specify the type of agency arrangement

**Important:** An agency agreement must be in writing and signed by all parties before any marketing activities commence.`,
  },
  {
    id: 'disclosure',
    title: 'Disclosure Obligations',
    icon: AlertTriangle,
    description: 'What must be disclosed to buyers and vendors',
    content: `**Disclosure Requirements**

Real estate agents have significant disclosure obligations under the Real Estate Agents Act 2008.

**To Vendors:**
• Any interest you or your agency has in the property
• All offers received (unless instructed otherwise in writing)
• Material information about potential buyers
• Any relationship with the buyer

**To Buyers:**
• Known defects or issues with the property
• Any LIM or building report information you're aware of
• Information about the vendor's motivations (if permitted)
• Any agency relationship with the vendor

**Multi-offer situations require specific disclosures to all parties.**`,
  },
  {
    id: 'advertising',
    title: 'Advertising Rules',
    icon: BookOpen,
    description: 'Compliant property advertising guidelines',
    content: `**Advertising Compliance**

All property advertising must comply with the Fair Trading Act and REA guidelines.

**Key Rules:**
• Prices must not be misleading
• "Deadline sale" must have genuine deadline
• Photos must be recent and accurate
• Floor plans must be accurate
• Statistical claims must be verifiable

**Prohibited:**
• Bait pricing
• False urgency claims
• Misleading property descriptions
• Doctored or enhanced photos
• Claims without vendor authority

**Digital advertising** follows the same rules as traditional media.`,
  },
  {
    id: 'multioffer',
    title: 'Multi-offers',
    icon: Shield,
    description: 'Managing multiple offer situations',
    content: `**Multi-Offer Process**

When you receive multiple offers on a property:

**Step 1: Notify All Parties**
Inform all potential buyers that multiple offers have been received.

**Step 2: Explain the Process**
Clearly explain how the vendor intends to handle the situation.

**Step 3: Maintain Confidentiality**
Do not disclose terms of competing offers without permission.

**Step 4: Present All Offers**
Submit all offers to the vendor simultaneously if possible.

**Step 5: Document Everything**
Keep detailed records of all communications and decisions.

**Vendor Instructions:** Always get written instructions on how the vendor wants to handle multi-offer situations.`,
  },
  {
    id: 'aml',
    title: 'AML Obligations',
    icon: Shield,
    description: 'Anti-money laundering requirements',
    content: `**AML/CFT Requirements**

Real estate agents are reporting entities under the AML/CFT Act.

**Customer Due Diligence (CDD):**
• Verify identity of all clients using approved methods
• Understand the nature and purpose of the transaction
• Maintain ongoing monitoring
• Keep records for 5+ years

**Enhanced Due Diligence:**
Required for high-risk clients, politically exposed persons, or unusual transactions.

**Suspicious Activity Reports:**
File SARs for any transaction that appears unusual or potentially suspicious.

**Compliance Programme:**
Your agency must have a written AML/CFT compliance programme, designated compliance officer, and regular staff training.`,
  },
  {
    id: 'complaints',
    title: 'Complaints Handling',
    icon: AlertTriangle,
    description: 'Managing complaints professionally',
    content: `**Complaints Process**

Every complaint should be taken seriously and handled professionally.

**Initial Response:**
• Acknowledge receipt within 24 hours
• Assign a responsible person
• Set expectations for resolution timeline

**Investigation:**
• Gather all relevant facts
• Document all communications
• Maintain confidentiality

**Resolution:**
• Propose fair resolution options
• Document the outcome
• Follow up to ensure satisfaction

**Escalation:**
If unable to resolve internally, complaints may be escalated to:
• REA (Real Estate Authority)
• CAC (Complaints Assessment Committee)
• READT (Tribunal)

**Keep comprehensive records of all complaint handling.**`,
  },
]

const preListingChecklist: ChecklistItem[] = [
  { id: '1', label: 'Agency agreement signed by all vendors', checked: false },
  { id: '2', label: 'Vendor disclosure form completed', checked: false },
  { id: '3', label: 'Marketing authority obtained', checked: false },
  { id: '4', label: 'Price expectation documented', checked: false },
  { id: '5', label: 'AML identity verification completed', checked: false },
  { id: '6', label: 'Property photos taken and approved', checked: false },
  { id: '7', label: 'LIM ordered or discussed with vendor', checked: false },
  { id: '8', label: 'Building report status confirmed', checked: false },
]

export function ComplianceScreen() {
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null)
  const [checklist, setChecklist] = useState(preListingChecklist)

  const toggleChecklistItem = (id: string) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item)),
    )
  }

  const completedCount = checklist.filter((item) => item.checked).length
  const guide = selectedGuide ? guides.find((g) => g.id === selectedGuide) : null

  if (guide) {
    return (
      <div className="pb-4">
        <button
          onClick={() => setSelectedGuide(null)}
          className="flex items-center gap-2 text-primary text-sm mb-4 hover:underline"
        >
          <ChevronDown className="w-4 h-4 rotate-90" />
          Back to Compliance Hub
        </button>

        <Card className="p-4 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <guide.icon className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">{guide.title}</h2>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground">
            {guide.content.split('\n').map((line, i) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return (
                  <h3 key={i} className="font-semibold text-foreground mt-4 first:mt-0 mb-2">
                    {line.replace(/\*\*/g, '')}
                  </h3>
                )
              }
              if (line.startsWith('• ')) {
                return (
                  <p key={i} className="pl-2 py-0.5">
                    {line}
                  </p>
                )
              }
              return (
                <p key={i} className="leading-relaxed">
                  {line}
                </p>
              )
            })}
          </div>
        </Card>

        <PoweredByFooter />
      </div>
    )
  }

  return (
    <div className="pb-4 space-y-6">
      {/* Checklist Section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-primary" />
            Pre-Listing Checklist
          </h2>
          <span className="text-sm text-muted-foreground">
            {completedCount}/{checklist.length}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-2 bg-secondary rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-accent transition-all duration-300"
            style={{ width: `${(completedCount / checklist.length) * 100}%` }}
          />
        </div>

        <Card className="p-4">
          <div className="space-y-3">
            {checklist.map((item) => (
              <label key={item.id} className="flex items-center gap-3 cursor-pointer group">
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={() => toggleChecklistItem(item.id)}
                  className="data-[state=checked]:bg-accent data-[state=checked]:border-accent"
                />
                <span
                  className={`text-sm transition-colors ${
                    item.checked
                      ? 'text-muted-foreground line-through'
                      : 'text-foreground group-hover:text-primary'
                  }`}
                >
                  {item.label}
                </span>
              </label>
            ))}
          </div>
        </Card>
      </section>

      {/* Guides Section */}
      <section>
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-primary" />
          Compliance Guides
        </h2>

        <div className="space-y-3">
          {guides.map((guide) => {
            const Icon = guide.icon
            return (
              <Card
                key={guide.id}
                className="p-4 cursor-pointer card-interactive"
                onClick={() => {
                  trackEvent('compliance_guide_opened', { guide: guide.title })
                  setSelectedGuide(guide.id)
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground">{guide.title}</h3>
                    <p className="text-sm text-muted-foreground truncate">{guide.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                </div>
              </Card>
            )
          })}
        </div>
      </section>

      {/* Quick Scenarios */}
      <section>
        <h2 className="text-lg font-semibold mb-3">What To Do When...</h2>
        <Accordion type="single" collapsible className="space-y-2">
          <AccordionItem value="conflict" className="border rounded-lg px-4">
            <AccordionTrigger className="text-sm font-medium hover:no-underline">
              A buyer and seller are in conflict
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              Remain neutral and professional. Document all communications. Remind both parties of
              the terms of their agreements. If necessary, seek guidance from your branch manager or
              REINZ.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="defect" className="border rounded-lg px-4">
            <AccordionTrigger className="text-sm font-medium hover:no-underline">
              You discover a property defect
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              Immediately inform the vendor and document the discovery. Discuss disclosure
              obligations with them. Material defects must be disclosed to potential buyers.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="suspicious" className="border rounded-lg px-4">
            <AccordionTrigger className="text-sm font-medium hover:no-underline">
              A transaction seems suspicious
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              Do not alert the client. Contact your compliance officer immediately. File a
              Suspicious Activity Report (SAR) with the Financial Intelligence Unit if required.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <PoweredByFooter />
    </div>
  )
}
